import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Factory, Boxes, Package, Wrench, ClipboardList,
  Truck, Timer, Users, Building2, Cog, ShoppingCart, ShieldCheck,
  FlaskConical, LineChart, ScrollText, LogOut, Bell, Menu, Search, User, X, RefreshCw,
  Warehouse, ArrowUpFromLine, ArrowDownToLine, CalendarDays } from "lucide-react";
import { ROLE_LABEL } from "@/lib/api";

const nav = [
  { section: "Overview", items: [
    { to: "/order-tracker", label: "Order Tracker", icon: FlaskConical, testid: "sidebar-nav-order-tracker" },
    { to: "/dashboard", label: "CEO Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
    { to: "/shop-floor", label: "Shop Floor Live", icon: Factory, testid: "sidebar-nav-shop-floor" },
  ]},
  { section: "Sales & Billing", items: [
    { to: "/sales-orders", label: "Sales Orders", icon: ShoppingCart, testid: "sidebar-nav-sales-orders" },
    { to: "/invoices", label: "Invoices", icon: ScrollText, testid: "sidebar-nav-invoices" },
    { to: "/aged-receivables", label: "Aged Receivables", icon: ScrollText, testid: "sidebar-nav-aged-receivables" },
  ]},
  { section: "Purchase", items: [
    { to: "/purchase-orders-supplier", label: "Purchase Orders", icon: ShoppingCart, testid: "sidebar-nav-purchase-orders" },
    { to: "/grn", label: "GRN / Receipts", icon: Truck, testid: "sidebar-nav-grn" },
  ]},
  { section: "Manufacturing", items: [
    { to: "/production-orders", label: "Production Orders", icon: ClipboardList, testid: "sidebar-nav-production-orders" },
    { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "sidebar-nav-schedule" },
    { to: "/rm-planning", label: "RM Planning", icon: LineChart, testid: "sidebar-nav-rm-planning" },
    { to: "/production-entry", label: "Production Entry", icon: LineChart, testid: "sidebar-nav-production-entry" },
    { to: "/downtime", label: "Downtime", icon: Timer, testid: "sidebar-nav-downtime" },
    { to: "/qc", label: "Quality Control", icon: ShieldCheck, testid: "sidebar-nav-qc" },
    { to: "/traceability", label: "Batch Traceability", icon: FlaskConical, testid: "sidebar-nav-traceability" },
  ]},
  { section: "Masters", items: [
    { to: "/products", label: "Products", icon: Package, testid: "sidebar-nav-products" },
    { to: "/bom", label: "BOM", icon: ScrollText, testid: "sidebar-nav-bom" },
    { to: "/machines", label: "Machines", icon: Cog, testid: "sidebar-nav-machines" },
    { to: "/moulds", label: "Moulds", icon: Wrench, testid: "sidebar-nav-moulds" },
    { to: "/raw-materials", label: "Raw Materials", icon: Boxes, testid: "sidebar-nav-raw-materials" },
    { to: "/warehouses", label: "Warehouses", icon: Building2, testid: "sidebar-nav-warehouses" },
    { to: "/suppliers", label: "Suppliers", icon: ShoppingCart, testid: "sidebar-nav-suppliers" },
    { to: "/customers", label: "Customers", icon: Users, testid: "sidebar-nav-customers" },
    { to: "/units", label: "Units", icon: Building2, testid: "sidebar-nav-units", roles: ["CEO_ADMIN"] },
  ]},
  { section: "Inventory & Ops", items: [
    { to: "/rm-stock", label: "RM Stock", icon: Warehouse, testid: "sidebar-nav-rm-stock" },
    { to: "/rm-issues", label: "RM Issues", icon: ArrowUpFromLine, testid: "sidebar-nav-rm-issues" },
    { to: "/rm-returns", label: "RM Returns", icon: ArrowDownToLine, testid: "sidebar-nav-rm-returns" },
    { to: "/fg-inventory", label: "FG Inventory", icon: Boxes, testid: "sidebar-nav-fg-inventory" },
    { to: "/dispatch", label: "Dispatch", icon: Truck, testid: "sidebar-nav-dispatch" },
  ]},
  { section: "Admin", roles: ["CEO_ADMIN"], items: [
    { to: "/zoho-sync", label: "Zoho Sync", icon: RefreshCw, testid: "sidebar-nav-zoho-sync", roles: ["CEO_ADMIN"] },
    { to: "/data-import", label: "Data Import", icon: Boxes, testid: "sidebar-nav-data-import", roles: ["CEO_ADMIN"] },
    { to: "/users", label: "Users & Roles", icon: Users, testid: "sidebar-nav-users", roles: ["CEO_ADMIN"] },
    { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "sidebar-nav-audit", roles: ["CEO_ADMIN"] },
  ]},
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);

  const canSee = (roles) => !roles || roles.includes(user?.role);

  const SidebarBody = ({compact}) => (
    <>
      <div className="px-4 py-4 flex items-center gap-2 border-b border-white/5 justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center text-white font-bold">S</div>
          {!compact && <div className="leading-tight">
            <div className="text-sm font-semibold text-white">Sangam Plastic</div>
            <div className="text-[10px] tracking-wider text-slate-400 uppercase">ERP / MES</div>
          </div>}
        </div>
        <button className="md:hidden text-slate-400" onClick={()=>setMobileOpen(false)} data-testid="sidebar-close-mobile"><X size={18}/></button>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        {nav.filter(s=>canSee(s.roles)).map((s) => (
          <div key={s.section}>
            {!compact && <div className="nav-section">{s.section}</div>}
            {s.items.filter(it=>canSee(it.roles)).map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={it.testid}
                onClick={()=>setMobileOpen(false)}
                className={({isActive}) => `nav-item mx-2 my-0.5 ${isActive ? "active" : ""}`}>
                <it.icon size={16} />
                {!compact && <span>{it.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className={`sidebar ${collapsed ? "w-16" : "w-64"} shrink-0 hidden md:flex flex-col transition-all`} data-testid="app-sidebar">
        <SidebarBody compact={collapsed}/>
        <button data-testid="sidebar-collapse-btn" onClick={()=>setCollapsed(!collapsed)} className="text-slate-400 text-xs py-2 border-t border-white/5 hover:text-white">
          {collapsed ? "»" : "« Collapse"}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={()=>setMobileOpen(false)}/>
          <aside className="fixed inset-y-0 left-0 w-64 z-50 sidebar flex md:hidden flex-col" data-testid="mobile-sidebar">
            <SidebarBody compact={false}/>
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-4 sticky top-0 z-30">
          <button data-testid="mobile-menu-btn" className="md:hidden text-slate-600" onClick={()=>setMobileOpen(true)}><Menu size={20}/></button>
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="global-search" placeholder="Search products, batches, orders..." className="w-full pl-9 pr-3 py-1.5 text-sm rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-sky-400"/>
          </div>
          <button data-testid="notifications-btn" className="relative text-slate-500 hover:text-slate-900"><Bell size={18}/></button>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600"><User size={14}/></div>
            <div className="hidden sm:block leading-tight">
              <div className="font-medium text-slate-900" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500" data-testid="header-user-role">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <button data-testid="btn-logout" onClick={logout} className="ml-2 text-slate-500 hover:text-red-600" title="Logout"><LogOut size={16}/></button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 bg-slate-50 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
